import { BaseDeckManagementEntry, createChangeSetOperations, KPIDriversInputSchema } from '@schema/Common.schema';
import { DenominatorLabelValues } from '@services/api/deckManagement/graphql/schema/API';
import { z } from 'zod';

/**
 * Page Schema for the SaveDeck API operation.
 *
 * @todo The types of {@link Page#previous_page_id} and {@link Page#next_page_id} do not match the SaveDeck types
 *   of {@link PageItemsInput#previous_page_id} and {@link PageItemsInput#next_page_id}. Cannot include those props on
 *   this schema until the types are updated to match.
 */
export const PageSchema = BaseDeckManagementEntry.extend({
  page_id: z.string(),
  // A LIST of the union wrapper — one entry per configured dataset. See KPIDriversInputSchema.
  page_drivers: z.array(KPIDriversInputSchema).optional(),
  page_name: z.string().optional(),
  deck_id: z.string().optional(),
  page_grain: z.string().optional(),
  page_no: z.number().optional(),
  page_type: z.string().optional(),
  denominator_label: z.enum(DenominatorLabelValues).nullable().optional(),
  /**
   * Reusable PageLayout family. When non-empty, getPageLayout resolves this
   * Page grain through TEMPLATE#{layout_family}#{deck_cycle}#{grain}.
   */
  layout_family: z.string().optional(),

  // Uncomment once PageItemsInput is updated (see above comment)
  // previous_page_id: z.string().optional(),
  // next_page_id: z.string().optional(),
});

/**
 * Schema for creating new Page entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches {@link PageItemsInput} GraphQL type requirements.
 */
export const CreatePageSchema = PageSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for creating new Page entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches {@link PageItemsInput} GraphQL type requirements.
 */
export const UpdatePageSchema = PageSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for deleting existing Pages
 * Includes only the primary fields needed for {@link PageDeleteItemsInput}
 */
export const DeletePageSchema = z.object({
  page_id: z.string(),
});

/**
 * Complete changeset schema for Page operations.
 * Provides create, update, and delete arrays with appropriate validation.
 */
export const PageChangeSetSchema = createChangeSetOperations({
  create: CreatePageSchema,
  update: UpdatePageSchema,
  delete: DeletePageSchema,
});

/**
 * Type definitions for use in components and services
 */

/**
 * Type for Page entries in create operations (without pk/sk)
 */
export type CreatePageType = z.infer<typeof CreatePageSchema>;

/**
 * Type for Page entries in update operation (without pk/sk)
 */
export type UpdatePageType = z.infer<typeof UpdatePageSchema>;

/**
 * Type for Page entries in delete operation
 */
export type DeletePageType = z.infer<typeof DeletePageSchema>;

/**
 * Type for the complete Page changeset structure
 */
export type PageChangeSetType = z.infer<typeof PageChangeSetSchema>;
